<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-lg-12">
        <div class="d-flex flex-wrap align-items-center justify-content-between my-schedule mb-4">
          <div class="d-flex align-items-center justify-content-between">
            <h4 class="font-weight-bold">Gemini API 사용량</h4>
          </div>
          <div class="d-flex align-items-center">
            <button class="btn btn-primary btn-sm mr-2" @click="fetchUsage">
              <i class="fas fa-sync-alt mr-1"></i> 새로고침
            </button>
            <span class="text-muted small">마지막 업데이트: {{ lastUpdated }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 날짜 필터 -->
    <div class="row mb-3">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-body py-2">
            <div class="d-flex flex-wrap align-items-center">
              <!-- 빠른 선택 버튼 -->
              <div class="btn-group mr-3 mb-2">
                <button
                  class="btn btn-sm"
                  :class="dateRange === 'today' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="setDateRange('today')"
                >오늘</button>
                <button
                  class="btn btn-sm"
                  :class="dateRange === 'yesterday' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="setDateRange('yesterday')"
                >어제</button>
                <button
                  class="btn btn-sm"
                  :class="dateRange === 'week' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="setDateRange('week')"
                >최근 7일</button>
                <button
                  class="btn btn-sm"
                  :class="dateRange === 'month' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="setDateRange('month')"
                >최근 30일</button>
                <button
                  class="btn btn-sm"
                  :class="dateRange === 'custom' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="dateRange = 'custom'"
                >직접 선택</button>
              </div>

              <!-- 날짜 입력 -->
              <div v-if="dateRange === 'custom'" class="d-flex align-items-center mb-2">
                <input
                  type="date"
                  class="form-control form-control-sm mr-2"
                  v-model="startDate"
                  style="width: 150px;"
                >
                <span class="mx-1">~</span>
                <input
                  type="date"
                  class="form-control form-control-sm mr-2"
                  v-model="endDate"
                  style="width: 150px;"
                >
                <button class="btn btn-sm btn-success" @click="fetchUsage">
                  <i class="fas fa-search"></i> 조회
                </button>
              </div>

              <!-- 현재 조회 기간 표시 -->
              <span class="ml-auto text-muted small mb-2">
                조회 기간: {{ displayDateRange }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 요약 카드 -->
    <div class="row mb-4">
      <div class="col-md-2">
        <div class="card bg-primary text-white">
          <div class="card-body text-center py-3">
            <h6 class="card-title mb-1">오늘 날짜</h6>
            <h5 class="mb-0">{{ todayDate }}</h5>
          </div>
        </div>
      </div>
      <div class="col-md-2">
        <div class="card bg-info text-white">
          <div class="card-body text-center py-3">
            <h6 class="card-title mb-1">총 회사 수</h6>
            <h5 class="mb-0">{{ usageData.length }}</h5>
          </div>
        </div>
      </div>
      <div class="col-md-2">
        <div class="card bg-success text-white">
          <div class="card-body text-center py-3">
            <h6 class="card-title mb-1">총 API 호출</h6>
            <h5 class="mb-0">{{ totalApiCalls.toLocaleString() }}</h5>
          </div>
        </div>
      </div>
      <div class="col-md-2">
        <div class="card bg-secondary text-white">
          <div class="card-body text-center py-3">
            <h6 class="card-title mb-1">총 토큰</h6>
            <h5 class="mb-0">{{ formatTokens(totalTokens) }}</h5>
          </div>
        </div>
      </div>
      <div class="col-md-2">
        <div class="card bg-danger text-white">
          <div class="card-body text-center py-3">
            <h6 class="card-title mb-1">총 비용</h6>
            <h5 class="mb-0">${{ totalCost.toFixed(4) }}</h5>
          </div>
        </div>
      </div>
      <div class="col-md-2">
        <div class="card bg-warning text-dark">
          <div class="card-body text-center py-3">
            <h6 class="card-title mb-1">일일 제한</h6>
            <h5 class="mb-0">{{ dailyLimit }}/회사</h5>
          </div>
        </div>
      </div>
    </div>

    <!-- RPM 상태 -->
    <div class="row mb-4">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-body py-2">
            <div class="d-flex align-items-center">
              <span class="mr-3"><strong>현재 RPM:</strong></span>
              <div class="progress flex-grow-1 mr-3" style="height: 25px;">
                <div
                  class="progress-bar"
                  :class="rpmProgressClass"
                  :style="{ width: rpmPercent + '%' }"
                >
                  {{ rpm.currentRPM }} / {{ rpm.limit }} ({{ rpmPercent }}%)
                </div>
              </div>
              <span class="badge" :class="rpmBadgeClass">
                남은 요청: {{ rpm.remaining }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 데이터 테이블 -->
    <div class="row">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">회사별 일일 사용량 (사용량 내림차순)</h5>
          </div>
          <div class="card-body">
            <div v-if="loading" class="text-center py-5">
              <b-spinner variant="primary" label="Loading..."></b-spinner>
              <p class="mt-2">데이터 로딩 중...</p>
            </div>
            <div v-else-if="usageData.length === 0" class="text-center py-5 text-muted">
              <i class="fas fa-database fa-3x mb-3"></i>
              <p>오늘 사용 데이터가 없습니다.</p>
            </div>
            <CDataTable
                storage-key="AdminGeminiUsage"
              v-else
              :headers="headers"
              :items="usageData"
              itemKey="companyId"
              :items-per-page="50"
              :show-select="false"
              :height="'500px'"
            >
              <!-- 사용량 프로그레스 바 -->
              <template v-slot:사용량="{ item }">
                <div class="d-flex align-items-center">
                  <div class="progress flex-grow-1 mr-2" style="height: 20px;">
                    <div
                      class="progress-bar"
                      :class="getProgressBarClass(item)"
                      :style="{ width: getUsagePercent(item) + '%' }"
                    >
                      {{ item.count }} / {{ item.limit }}
                    </div>
                  </div>
                  <span class="badge" :class="getBadgeClass(item)">
                    {{ getUsagePercent(item) }}%
                  </span>
                </div>
              </template>

              <!-- 남은 횟수 -->
              <template v-slot:남은횟수="{ item }">
                <span :class="item.remaining < 100 ? 'text-danger font-weight-bold' : 'text-success'">
                  {{ item.remaining }}
                </span>
              </template>

              <!-- 토큰 사용량 -->
              <template v-slot:토큰="{ item }">
                <div class="small">
                  <span class="text-primary">↑{{ formatTokens(item.totalInputTokens || 0) }}</span>
                  <span class="mx-1">/</span>
                  <span class="text-success">↓{{ formatTokens(item.totalOutputTokens || 0) }}</span>
                </div>
              </template>

              <!-- 비용 -->
              <template v-slot:비용="{ item }">
                <span class="text-danger font-weight-bold">${{ (item.totalCost || 0).toFixed(4) }}</span>
              </template>

              <!-- 평균 처리시간 -->
              <template v-slot:평균처리시간="{ item }">
                <span>{{ item.avgProcessingTime }}ms</span>
              </template>

              <!-- 마지막 업데이트 -->
              <template v-slot:마지막요청="{ item }">
                <span class="small">{{ formatTime(item.updatedAt) }}</span>
              </template>
            </CDataTable>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import CDataTable from "@/components/common/CDataTable";

export default {
  name: 'AdminGeminiUsage',
  components: {
    CDataTable
  },
  data() {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    return {
      loading: false,
      usageData: [],
      dailyLimit: 1000,
      lastUpdated: '-',
      dateRange: 'today',
      startDate: todayStr,
      endDate: todayStr,
      rpm: { currentRPM: 0, limit: 3000, remaining: 3000 },
      headers: [
        { text: '날짜', value: 'date', isSlot: false },
        { text: '회사ID', value: 'companyId', isSlot: false },
        { text: '사용량', value: '사용량', isSlot: true },
        { text: '호출횟수', value: 'count', isSlot: false },
        { text: '남은횟수', value: '남은횟수', isSlot: true },
        { text: '토큰(입력/출력)', value: '토큰', isSlot: true },
        { text: '비용(USD)', value: '비용', isSlot: true },
        { text: '평균처리시간', value: '평균처리시간', isSlot: true },
        { text: '마지막요청', value: '마지막요청', isSlot: true }
      ]
    };
  },
  computed: {
    todayDate() {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    },
    totalApiCalls() {
      return this.usageData.reduce((sum, item) => sum + (item.count || 0), 0);
    },
    totalTokens() {
      return this.usageData.reduce((sum, item) => sum + (item.totalTokens || 0), 0);
    },
    totalCost() {
      return this.usageData.reduce((sum, item) => sum + (item.totalCost || 0), 0);
    },
    displayDateRange() {
      if (this.startDate === this.endDate) {
        return this.startDate;
      }
      return `${this.startDate} ~ ${this.endDate}`;
    },
    rpmPercent() {
      return Math.round((this.rpm.currentRPM / this.rpm.limit) * 100);
    },
    rpmProgressClass() {
      if (this.rpmPercent >= 90) return 'bg-danger';
      if (this.rpmPercent >= 70) return 'bg-warning';
      return 'bg-success';
    },
    rpmBadgeClass() {
      if (this.rpmPercent >= 90) return 'badge-danger';
      if (this.rpmPercent >= 70) return 'badge-warning';
      return 'badge-success';
    }
  },
  mounted() {
    this.fetchUsage();
  },
  methods: {
    formatDateStr(date) {
      const d = date || new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    },
    setDateRange(range) {
      this.dateRange = range;
      const today = new Date();

      switch (range) {
        case 'today':
          this.startDate = this.formatDateStr(today);
          this.endDate = this.formatDateStr(today);
          break;
        case 'yesterday': {
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          this.startDate = this.formatDateStr(yesterday);
          this.endDate = this.formatDateStr(yesterday);
          break;
        }
        case 'week': {
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 6);
          this.startDate = this.formatDateStr(weekAgo);
          this.endDate = this.formatDateStr(today);
          break;
        }
        case 'month': {
          const monthAgo = new Date(today);
          monthAgo.setDate(monthAgo.getDate() - 29);
          this.startDate = this.formatDateStr(monthAgo);
          this.endDate = this.formatDateStr(today);
          break;
        }
      }

      this.fetchUsage();
    },
    async fetchUsage() {
      this.loading = true;
      try {
        const params = {
          startDate: this.startDate,
          endDate: this.endDate
        };
        const response = await axios.get('/api/admin/gemini-usage', { params });
        if (response.data.success) {
          this.usageData = response.data.data || [];
          this.dailyLimit = response.data.limit || 1000;
          this.lastUpdated = new Date().toLocaleTimeString('ko-KR');
          // RPM 상태 업데이트
          if (response.data.rpm) {
            this.rpm = response.data.rpm;
          }
        }
      } catch (error) {
        console.error('사용량 조회 오류:', error);
        this.$bvToast.toast('사용량 조회에 실패했습니다.', {
          title: '오류',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.loading = false;
      }
    },
    getUsagePercent(item) {
      return Math.round((item.count / item.limit) * 100);
    },
    getProgressBarClass(item) {
      const percent = this.getUsagePercent(item);
      if (percent >= 90) return 'bg-danger';
      if (percent >= 70) return 'bg-warning';
      return 'bg-success';
    },
    getBadgeClass(item) {
      const percent = this.getUsagePercent(item);
      if (percent >= 90) return 'badge-danger';
      if (percent >= 70) return 'badge-warning';
      return 'badge-success';
    },
    formatTime(dateStr) {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleTimeString('ko-KR');
    },
    formatTokens(tokens) {
      if (tokens >= 1000000) {
        return (tokens / 1000000).toFixed(2) + 'M';
      } else if (tokens >= 1000) {
        return (tokens / 1000).toFixed(1) + 'K';
      }
      return tokens.toLocaleString();
    }
  }
};
</script>

<style scoped>
.progress {
  border-radius: 10px;
}
.progress-bar {
  font-size: 12px;
  font-weight: bold;
}
</style>

